if (!customElements.get('card-product-slider')) {
  class CardProductSlider extends HTMLElement {
    constructor() {
      super();

      const swiperOptions = JSON.parse(this.getAttribute('data-swiper-options')) || {};

      this.sliderWrapper = this.querySelector('.swiper-wrapper');
      this.requiresFetch = this.hasAttribute('data-url');
      this.parent = this.closest('[data-section-id]');
      this.sliderOptions = {
        a11y: false,
        threshold: 3,
        spaceBetween: swiperOptions.spaceBetweenMobile || 2,
        slidesPerView: 'auto',
        resistanceRatio: 0.72,
        navigation: {
          nextEl: '.swiper-button--next',
          prevEl: '.swiper-button--prev'
        },
        breakpoints: {
          750: {
            slidesPerView: 3,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 2
          },
          1100: {
            slidesPerView: 4,
            spaceBetween: swiperOptions.spaceBetweenDesktop || 2
          }
        }
      };
    }

    connectedCallback() {
      if (this.requiresFetch) {
        this.fetchItems();
        return;
      }
      this.initSlider();
    }

    fetchItems() {
      if (!this.parent) throw new Error('Parent is missing');
      fetch(this.getAttribute('data-url'))
        .then(response => response.text())
        .then(text => {
          const html = new DOMParser().parseFromString(
            text,
            'text/html'
          );
          const wrapper = html.querySelector('.swiper-wrapper');

          if (wrapper && wrapper.innerHTML.trim().length) {
            this.querySelector('.swiper-wrapper').innerHTML =
              wrapper.innerHTML;
            this.parent.classList.remove('hidden');
            this.initSlider();
          }
        })
        .catch(e => {
          console.error(e);
        });
    }

    initSlider(slider = this) {
      if (this.slider) return;
      this.slider = new Swiper(slider, this.sliderOptions);

      this.setupAutoShuffle();

      const isShopTheLook = this.classList.contains('section-shop-the-look__slider')
      if (isShopTheLook) return;

      this.slider.wrapperEl.setAttribute('aria-live', 'polite');
      this.slider.wrapperEl.id = `swiper-wrapper-${this.dataset.sectionId}`;
      this.slider.slides.forEach((slide, i) => {
        slide.setAttribute('role', 'group');
        slide.setAttribute(
          'aria-label',
          `${i + 1} / ${this.slider.slides.length}`
        );
        slide.dataset.index = i;
      });
      const sliderButtons = [
        this.slider.navigation.prevEl,
        this.slider.navigation.nextEl
      ];

      sliderButtons.forEach((button, i) => {
        let type = 'Previous';

        if (i === 1) {
          type = 'Next';
        }

        button.setAttribute('aria-label', `${type} slide`);
        button.setAttribute(
          'aria-controls',
          this.slider.wrapperEl.id
        );
      });

      this.slider.wrapperEl.addEventListener('focusin', e => {
        // if clicked element is a button, input, textarea, select, prevent scrolling
        if (e.target.closest('button, a, input, textarea, select')) return;

        this.slider.el.scrollLeft = 0;

        const slide = e.target.closest('.swiper-slide');
        if (!slide) return;

        const slideIndex = +slide.dataset.index;
        const isFirstSlide = slideIndex === 0;
        const isLastSlide = slideIndex === this.slider.slides.length - 1;

        if (isFirstSlide || isLastSlide) {
          this.slider.slideTo(slideIndex, 0);
          return;
        }

        const slideRect = slide.getBoundingClientRect();
        const wrapperWidth = this.slider.wrapperEl.clientWidth;
        const slideLeft = slideRect.x + slideRect.width - (document.body.clientWidth - wrapperWidth) / 2;
        const isSlideVisible = slideLeft <= wrapperWidth && slideLeft >= slideRect.width;

        if (isSlideVisible) return;

        slideLeft >= slideRect.width
          ? this.slider.slideNext(0)
          : this.slider.slidePrev(0);
      });
    }

    setupAutoShuffle() {
      const raw = this.getAttribute('data-shuffle-interval');
      const intervalMs = parseInt(raw, 10);
      if (!intervalMs || intervalMs < 1000) return;
      if (!this.slider || !this.slider.wrapperEl) return;

      const wrapper = this.slider.wrapperEl;
      let paused = false;

      const pauseFor = (ms) => {
        paused = true;
        clearTimeout(this._shufflePauseTimer);
        this._shufflePauseTimer = setTimeout(() => { paused = false; }, ms);
      };

      ['pointerdown', 'touchstart', 'mouseenter', 'focusin'].forEach((evt) => {
        this.addEventListener(evt, () => pauseFor(intervalMs * 2), { passive: true });
      });

      const shuffleSlides = () => {
        if (paused) return;
        if (document.hidden) return;
        if (this.offsetParent === null) return;

        const slides = Array.from(wrapper.children);
        if (slides.length < 2) return;

        for (let i = slides.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [slides[i], slides[j]] = [slides[j], slides[i]];
        }

        const frag = document.createDocumentFragment();
        slides.forEach((slide, idx) => {
          slide.dataset.index = idx;
          slide.setAttribute('aria-label', `${idx + 1} / ${slides.length}`);
          frag.appendChild(slide);
        });
        wrapper.appendChild(frag);

        this.slider.update();
        this.slider.slideTo(0, 400);
      };

      this._shuffleTimer = setInterval(shuffleSlides, intervalMs);
    }

    disconnectedCallback() {
      if (this._shuffleTimer) {
        clearInterval(this._shuffleTimer);
        this._shuffleTimer = null;
      }
      if (this._shufflePauseTimer) {
        clearTimeout(this._shufflePauseTimer);
        this._shufflePauseTimer = null;
      }
    }
  }

  customElements.define('card-product-slider', CardProductSlider);
}

document.addEventListener('shopify:section:load', event => {
  const section = event.target.querySelector('card-product-slider');
  if (section) {
    section.connectedCallback();
  }
});
